import React, { useState } from 'react';
import PageTitle from '@components/pageTitle';
import translate from '@data/translations';
import dayjs from 'dayjs';
import { ReservationGuestBoard } from '@common/modelDefinition';
import { ReactComponent as Print } from '../../../assets/custom/icons/printer.svg';
import { ReactComponent as Meal } from '../../../assets/custom/icons/fork_knife.svg';
import { ReactComponent as Tick } from '../../../assets/custom/icons/tick.svg';
import { ReactComponent as Search } from '../../../assets/custom/icons/search.svg';
import {
    deleteReservationGuestBoards,
    getReservationGuestBoards,
    getReservationGuestBoardsPreview,
    printBoardConsumptions,
    saveReservationGuestBoards,
} from './api';
import { getGroupedReservationBoardsByRoomInfo, getGroupedReservationBoardsByType } from './utils';
import LoadingButton from '@components/buttons/loadingButton';
import PrintView from '@components/printView';
import useStore from '@data/state/zustand';
import { useParams } from 'react-router-dom';
import CustomInput from '@components/custom/input';
import InlineRangeInput from '@components/inlineRangeInput';

interface InvoicesProps {
    history: any;
    location: any;
}
export enum BoardEnum {
    'nb' = 'No board',
    'bb' = 'Breakfast',
    'hb' = 'Half board',
    'fb' = 'Full board',
    'ai' = 'All inclusive',
}
enum MealsEnum {
    'firstMeal' = 'Breakfast',
    'secondMeal' = 'Lunch',
    'thirdMeal' = 'Dinner',
}
interface SelectRoomGuests {
    [key: string]: number[];
}
function BoardConsumption(props: InvoicesProps) {
    const [searchTermState, setSearchTerm] = useState('');
    const [reservationGuestBoardsPreview, setReservationGuestBoardsPreview] = React.useState(
        [] as ReservationGuestBoard[]
    );
    const [reservationGuestBoardsConsumption, setReservationGuestBoardsConsumption] = React.useState(
        [] as ReservationGuestBoard[]
    );
    const [selectedRoomGuests, setSelectedRoomGuests] = React.useState({} as SelectRoomGuests);
    const [showGuests, setShowGuests] = React.useState(false as boolean);
    const [filterMeal, setFilterMeal] = React.useState('' as string);
    const systemLocale: string = useStore((appState) => appState.locale);

    const [from, setFrom] = React.useState(new Date(dayjs().startOf('day').valueOf()));
    const [to, setTo] = React.useState(new Date(dayjs().endOf('day').valueOf()));

    const { posTerminalId } = useParams<{ posTerminalId: string }>();

    React.useEffect(() => {
        const loadReservationGuestBoards = async () => {
            const _reservationGuestBoardsPreview = await getReservationGuestBoardsPreview(
                dayjs(from).startOf('day').valueOf(),
                dayjs(to).endOf('day').valueOf()
            );
            setReservationGuestBoardsPreview(_reservationGuestBoardsPreview);
            const _reservationGuestBoardsConsumption = await getReservationGuestBoards(dayjs().valueOf());
            setReservationGuestBoardsConsumption(_reservationGuestBoardsConsumption);
        };
        loadReservationGuestBoards().catch((err) => {
            console.log(err);
        });
    }, [from, to]);

    const handleDateSelection = (type: string, date: any) => {
        if (type === 'from') {
            setFrom(date);
        } else {
            setTo(date);
        }
    };

    const filterReservationGuestBoards =
        searchTermState.length > 0
            ? reservationGuestBoardsPreview.filter((r) => {
                  const guestFullName = `${r?.firstName} ${r?.lastName}`;
                  const guestFullNameByLastName = `${r?.lastName} ${r?.firstName}`;
                  if (
                      (guestFullName &&
                          guestFullName.toString().toLowerCase().includes(searchTermState.toString().toLowerCase())) ||
                      (guestFullNameByLastName &&
                          guestFullNameByLastName
                              .toString()
                              .toLowerCase()
                              .includes(searchTermState.toString().toLowerCase())) ||
                      (r.roomName &&
                          r.roomName.toString().toLowerCase().includes(searchTermState.toString().toLowerCase()))
                  ) {
                      return r;
                  } else {
                      return null;
                  }
              })
            : reservationGuestBoardsPreview;

    const filteredBySelectedType =
        filterMeal.length > 0
            ? filterReservationGuestBoards.filter((r) => {
                  if (r.type === filterMeal) {
                      return r;
                  } else {
                      return null;
                  }
              })
            : filterReservationGuestBoards;

    const groupedReservationBoardsByType = getGroupedReservationBoardsByType(filteredBySelectedType);
    const groupedReservationBoardsByTypeInfo = getGroupedReservationBoardsByType(reservationGuestBoardsPreview);

    // Helper: Izračunaj ukupan broj gostiju po obroku (zbraja po danima za period)
    const getTotalGuestsByMeal = (meal: string) => {
        const mealData = groupedReservationBoardsByTypeInfo[meal];
        if (!mealData) return { adults: 0, children: 0, total: 0 };

        // Grupiraj po roomInfoId + mealDate kombinaciji da se ispravno zbraja za period
        const groupedByRoomAndDate: { [key: string]: any } = {};
        mealData.forEach((item: any) => {
            const key = `${item.roomInfoId}_${item.mealDate || 'default'}`;
            if (!groupedByRoomAndDate[key]) {
                groupedByRoomAndDate[key] = item;
            }
        });

        let totalAdults = 0;
        let totalChildren = 0;

        Object.values(groupedByRoomAndDate).forEach((guest: any) => {
            totalAdults += guest.adultsNumber || 0;
            totalChildren += guest.childrenNumber || 0;
        });

        return { adults: totalAdults, children: totalChildren, total: totalAdults + totalChildren };
    };

    const breakfastStats = getTotalGuestsByMeal('firstMeal');
    const lunchStats = getTotalGuestsByMeal('secondMeal');
    const dinnerStats = getTotalGuestsByMeal('thirdMeal');

    // Provjera da li je odabran samo jedan dan (nije raspon)
    const isSingleDay = dayjs(from).isSame(dayjs(to), 'day');
    
    // Provjera da li je odabrani dan DANAS (samo tada se mogu označavati gosti)
    const isToday = isSingleDay && dayjs(from).isSame(dayjs(), 'day');

    if (!posTerminalId) {
        return (
            <div className="p-2 col-4 text-center border border-danger mt-16p">
                {translate('Check pos display config for access!')}
            </div>
        );
    }

    return (
        <div className="">
            <div className="d-flex h-100 app-flex-div unselectable">
                <div className="" id="main-app-div" style={{ width: '100%' }}>
                    <div className="title-with-options display-flex space-between d-print-none">
                        <PageTitle />
                    </div>
                    <div className="search-and-page-controls display-flex space-between align-center d-print-none">
                        <div className="flex-center w-100">
                            <InlineRangeInput
                                initialFrom={from}
                                initialTo={to}
                                onChange={handleDateSelection}
                                disableBefore={new Date(dayjs(to).subtract(30, 'days').valueOf())}
                            />
                            <div className="ml-12p">
                                <CustomInput
                                    className={'on-mouse-down-show'}
                                    onInputChange={(e: any) => {
                                        setSearchTerm(e.target.value);
                                    }}
                                    icon={<Search />}
                                    placeholder={translate('Search')}
                                />
                            </div>
                            <div
                                onClick={() => {
                                    setShowGuests(!showGuests);
                                }}
                                className={
                                    showGuests === true
                                        ? 'button-white-w-icon-only-selected ml-16p pt-8p pb-8p pr-8p pl-8p'
                                        : 'button-white-w-icon-only ml-16p pt-8p pb-8p pr-8p pl-8p'
                                }
                            >
                                {translate('Show guests')}
                            </div>
                        </div>
                        <div className="flex-center">
                            <div
                                className={
                                    filterMeal === 'firstMeal'
                                        ? 'button-white-w-icon-only-selected mr-8p'
                                        : 'button-white-w-icon-only mr-8p'
                                }
                                onClick={async () => {
                                    if (filterMeal === 'firstMeal') {
                                        setFilterMeal('');
                                    } else {
                                        setFilterMeal('firstMeal');
                                    }
                                }}
                            >
                                <Meal />
                                <span>{`${translate('Breakfast')}`}</span>
                                <div className="pr-8p pl-8p">{breakfastStats.total}</div>
                            </div>
                            <div
                                className={
                                    filterMeal === 'secondMeal'
                                        ? 'button-white-w-icon-only-selected mr-8p'
                                        : 'button-white-w-icon-only mr-8p'
                                }
                                onClick={async () => {
                                    if (filterMeal === 'secondMeal') {
                                        setFilterMeal('');
                                    } else {
                                        setFilterMeal('secondMeal');
                                    }
                                }}
                            >
                                <Meal />
                                <span>{`${translate('Lunch')}`}</span>
                                <div className="pr-8p pl-8p">{lunchStats.total}</div>
                            </div>
                            <div
                                className={
                                    filterMeal === 'thirdMeal'
                                        ? 'button-white-w-icon-only-selected mr-8p'
                                        : 'button-white-w-icon-only mr-8p'
                                }
                                onClick={async () => {
                                    if (filterMeal === 'thirdMeal') {
                                        setFilterMeal('');
                                    } else {
                                        setFilterMeal('thirdMeal');
                                    }
                                }}
                            >
                                <Meal />
                                <span>{`${translate('Dinner')}`}</span>
                                <div className="pr-8p pl-8p">{dinnerStats.total}</div>
                            </div>
                            <LoadingButton
                                disabled={false}
                                onClick={async () => {
                                    try {
                                        await printBoardConsumptions(
                                            reservationGuestBoardsPreview,
                                            dayjs(from).valueOf(),
                                            dayjs(to).valueOf()
                                        );
                                    } catch (error) {
                                        console.log(error);
                                    }
                                }}
                                className="button-white-w-icon-only mr-4p ml-4p"
                            >
                                <Print />
                                <span className="">{`${translate('Kitchen')}`}</span>
                            </LoadingButton>
                        </div>
                    </div>
                    <PrintView
                        items={
                            <div>
                                <h4 className="ml-16p">
                                    {dayjs(from).isSame(dayjs(to), 'day')
                                        ? dayjs(from).locale(systemLocale).format('ll')
                                        : `${dayjs(from).locale(systemLocale).format('ll')} — ${dayjs(to).locale(systemLocale).format('ll')}`
                                    }
                                </h4>
                                <div
                                    className="display-flex justify-content-between mr-16p ml-16p mt-16p mb-16p"
                                    style={{ height: '100%' }}
                                >
                                    {Object.keys(groupedReservationBoardsByType).map((meal) => {
                                        const _reservationBoardGuests = groupedReservationBoardsByType[meal];
                                        const groupedByRoom = getGroupedReservationBoardsByRoomInfo(_reservationBoardGuests);
                                        const mealStats = getTotalGuestsByMeal(meal);
                                        return (
                                            <div
                                                key={meal}
                                                className="pr-8p pl-8p mb-8p pt-8p mr-4p ml-4p"
                                                style={{
                                                    height: '100%',
                                                    flex: 1,
                                                    boxShadow: '0px 2px 5px rgba(105, 119, 140, 0.15)',
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <b>{translate(MealsEnum[meal as keyof typeof MealsEnum])}</b>
                                                    <span className="text-muted">
                                                        {`${translate('Adults')}: ${mealStats.adults} | ${translate('Children')}: ${mealStats.children}`}
                                                    </span>
                                                </div>
                                                {groupedByRoom && Object.keys(groupedByRoom).length > 0 ? (
                                                    <div>
                                                        <div className="custom-list-header">
                                                            <div>{translate('Name')}</div>
                                                            <div>{translate('Board')}</div>
                                                            <div>{translate('Consumption records')}</div>
                                                        </div>
                                                        {Object.keys(groupedByRoom).map((roomInfoId) => {
                                                            const reservationBoardGuests = groupedByRoom[roomInfoId];
                                                            if (!reservationBoardGuests) return null;

                                                            const board = reservationBoardGuests[0]?.board;
                                                            const roomName = reservationBoardGuests[0]?.roomName;
                                                            const adultsNumber = (reservationBoardGuests[0] as any)?.adultsNumber || 0;
                                                            const childrenNumber = (reservationBoardGuests[0] as any)?.childrenNumber || 0;
                                                            const totalExpected = adultsNumber + childrenNumber;

                                                            const enteredGuests = reservationBoardGuests.filter(
                                                                (g: ReservationGuestBoard) => g.firstName && g.lastName
                                                            );
                                                            const enteredCount = enteredGuests.length;
                                                            const missingCount = Math.max(0, totalExpected - enteredCount);

                                                            const roomExisting = isToday ? reservationGuestBoardsConsumption.filter(
                                                                (rgb) => Number(rgb.roomInfoId) === Number(roomInfoId) && rgb.type === meal
                                                            ) : [];
                                                            const isChecked = isToday && roomExisting.length === reservationBoardGuests.length;

                                                            return (
                                                                <div key={roomInfoId + 'room'}>
                                                                    <div
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            const newSelectedRoomGuests = { ...selectedRoomGuests };
                                                                            if (newSelectedRoomGuests[meal]?.includes(Number(roomInfoId))) {
                                                                                newSelectedRoomGuests[meal] = [];
                                                                            } else {
                                                                                newSelectedRoomGuests[meal] = [Number(roomInfoId)];
                                                                            }
                                                                            setSelectedRoomGuests(newSelectedRoomGuests);
                                                                        }}
                                                                        className="custom-list-item align-items-center pointer"
                                                                    >
                                                                        <div>
                                                                            <b>{`${translate('Room')} ${roomName}`}</b>
                                                                            <small className="ml-8p text-muted">({adultsNumber}+{childrenNumber})</small>
                                                                        </div>
                                                                        <div>{translate(BoardEnum[board as keyof typeof BoardEnum])}</div>
                                                                        <div>{isToday ? (isChecked ? <Tick /> : <div>{translate('Through the pos terminal')}</div>) : '-'}</div>
                                                                    </div>
                                                                    {/* Uneseni gosti */}
                                                                    {enteredGuests.map((r: ReservationGuestBoard, index: number) => {
                                                                        const show = (r.type && r.roomInfoId && selectedRoomGuests[r.type]?.includes(r.roomInfoId)) || showGuests;
                                                                        if (!show) return null;
                                                                        const isGuestChecked = isToday && reservationGuestBoardsConsumption.find(
                                                                            (rgbc) => rgbc.board === r.board && rgbc.firstName === r.firstName && r.lastName === rgbc.lastName && rgbc.type === r.type && rgbc.reservationId === r.reservationId && rgbc.roomInfoId === r.roomInfoId
                                                                        );
                                                                        return (
                                                                            <div
                                                                                onClick={async () => {
                                                                                    if (!isToday) return;
                                                                                    if (isGuestChecked) {
                                                                                        const res = await deleteReservationGuestBoards([isGuestChecked]);
                                                                                        setReservationGuestBoardsConsumption(res);
                                                                                    } else {
                                                                                        const res = await saveReservationGuestBoards([r]);
                                                                                        setReservationGuestBoardsConsumption(res);
                                                                                    }
                                                                                }}
                                                                                key={index}
                                                                                className={isToday ? "custom-list-item pointer" : "custom-list-item"}
                                                                                style={!isToday ? { cursor: 'default' } : undefined}
                                                                            >
                                                                                <div className="ml-16p">{r.firstName} {r.lastName}</div>
                                                                                <div>{translate(BoardEnum[r.board as keyof typeof BoardEnum])}</div>
                                                                                <div>{isToday ? (isGuestChecked ? <Tick /> : <div>{translate('Through the pos terminal')}</div>) : '-'}</div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    {/* Neuneseni gosti (dummy) */}
                                                                    {Array.from({ length: missingCount }).map((_, index) => {
                                                                        const show = (reservationBoardGuests[0]?.type && reservationBoardGuests[0]?.roomInfoId && selectedRoomGuests[reservationBoardGuests[0].type]?.includes(reservationBoardGuests[0].roomInfoId)) || showGuests;
                                                                        if (!show) return null;
                                                                        return (
                                                                            <div key={`missing-${index}`} className="custom-list-item" style={{ opacity: 0.5, fontStyle: 'italic', cursor: 'default' }}>
                                                                                <div className="ml-16p text-muted">{`${translate('Guest')} ${enteredCount + index + 1}`}</div>
                                                                                <div className="text-muted">{translate(BoardEnum[board as keyof typeof BoardEnum])}</div>
                                                                                <div className="text-muted">{isToday ? translate('Through the pos terminal') : '-'}</div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        }
                        documentHeader={<div className="col-12 mt-2"></div>}
                        documentFooter={<div className="col-12 mb-3"></div>}
                        footerHeight={52}
                        headerHeight={52}
                    />
                </div>
            </div>
        </div>
    );
}

export default BoardConsumption;